import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Receipt, CalendarClock, GripVertical } from "lucide-react";
import { eur, fdate, STAGE_STYLE, OPEN_STAGES } from "@/components/chef/crm/crmUtils";

// Phase 67 — pipeline kanban : glisser une carte vers une colonne change l'étape (gagné / perdu passent par le dialog pour le motif)
export function CrmPipeline({ stages, refreshKey, onChanged, onOpen, onOpenContact }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [dragOver, setDragOver] = useState(null);

  const load = async () => {
    try { const { data: r } = await api.get(`/chef/crm/opportunities?stage=all${q ? `&q=${encodeURIComponent(q)}` : ""}`); setData(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [q, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = async (o, stage) => {
    if (o.stage === stage) return;
    if (stage === "perdu") { onOpen({ ...o, _askLost: true }); return; }
    try { await api.post(`/chef/crm/opportunities/${o.id}/stage`, { stage }); toast.success(`« ${o.title} » → ${stages.find((s) => s.key === stage)?.label}`); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDrop = (e, stage) => { e.preventDefault(); setDragOver(null); const o = data?.items.find((x) => x.id === e.dataTransfer.getData("text/plain")); if (o) move(o, stage); };
  const cols = stages.filter((s) => showClosed || OPEN_STAGES.includes(s.key));
  const items = data?.items || [];

  return (
    <div className="space-y-3" data-testid="crm-pipeline">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#868E96]" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Affaire, contact, n° devis…" data-testid="crm-pipeline-search" className="h-9 pl-9 w-[240px]" /></div>
        <label className="flex items-center gap-2 text-xs cursor-pointer ml-auto"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="crm-pipeline-show-closed" className="h-4 w-4 accent-[#002FA7]" />Afficher gagnées / perdues</label>
      </div>
      <div className={`grid gap-3 ${showClosed ? "lg:grid-cols-6" : "lg:grid-cols-4"} sm:grid-cols-2`} data-testid="crm-pipeline-columns">
        {cols.map((s) => {
          const list = items.filter((o) => o.stage === s.key);
          const total = list.reduce((a, o) => a + (o.amount_ht || 0), 0);
          const st = STAGE_STYLE[s.key];
          return (
            <div key={s.key} onDragOver={(e) => { e.preventDefault(); setDragOver(s.key); }} onDragLeave={() => setDragOver(null)} onDrop={(e) => onDrop(e, s.key)} data-testid={`crm-col-${s.key}`}
              className={`bg-white border min-h-[220px] flex flex-col transition-colors ${dragOver === s.key ? "border-[#002FA7] bg-[#EFF3FF]" : "border-[#DEE2E6]"}`}>
              <div className={`px-3 py-2 flex items-center justify-between ${st.bg} ${st.text}`}>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold">{s.label}</span>
                <span className="text-[10px] font-mono" data-testid={`crm-col-count-${s.key}`}>{list.length} · {eur(total)}</span>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {list.length === 0 && <div className="text-[11px] text-[#868E96] text-center py-6" data-testid={`crm-col-empty-${s.key}`}>{dragOver === s.key ? "Déposer ici" : "—"}</div>}
                {list.map((o) => (
                  <div key={o.id} draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", o.id); e.dataTransfer.effectAllowed = "move"; }} data-testid={`crm-opp-${o.id}`}
                    className="border border-[#DEE2E6] bg-white p-2.5 hover:border-[#0A0A0A] cursor-grab active:cursor-grabbing group">
                    <div className="flex items-start gap-1.5">
                      <GripVertical className="h-3.5 w-3.5 text-[#DEE2E6] group-hover:text-[#868E96] shrink-0 mt-0.5" />
                      <button onClick={() => onOpen(o)} className="text-left flex-1 min-w-0" data-testid={`crm-opp-open-${o.id}`}>
                        <div className="text-sm font-semibold leading-snug line-clamp-2">{o.title}</div>
                      </button>
                    </div>
                    <button onClick={() => onOpenContact(o.contact_id)} className="text-[11px] text-[#002FA7] hover:underline truncate block max-w-full mt-1 ml-5" data-testid={`crm-opp-contact-${o.id}`}>{o.contact_name}</button>
                    <div className="flex items-center justify-between mt-1.5 ml-5 text-[11px]">
                      <span className="font-mono font-bold">{eur(o.amount_ht)} <span className="text-[#868E96] font-normal">HT</span></span>
                      <span className="px-1.5 py-0.5 text-[9px] font-black bg-[#F1F3F5]" title="Probabilité">{o.probability} %</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 ml-5 text-[10px] text-[#868E96]">
                      {o.expected_close && <span className={`inline-flex items-center gap-0.5 ${o.overdue ? "text-[#E60000] font-semibold" : ""}`} data-testid={`crm-opp-close-${o.id}`}><CalendarClock className="h-3 w-3" />{fdate(o.expected_close)}</span>}
                      {o.devis_id && <span className="inline-flex items-center gap-0.5" data-testid={`crm-opp-devis-${o.id}`}><Receipt className="h-3 w-3" />Devis</span>}
                    </div>
                    <select value={o.stage} onChange={(e) => move(o, e.target.value)} data-testid={`crm-opp-stage-${o.id}`} className="mt-2 ml-5 h-7 w-[calc(100%-1.25rem)] text-[11px] border border-[#DEE2E6] bg-white px-1 lg:hidden" aria-label="Changer d'étape">
                      {stages.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {data && items.length === 0 && <div className="text-center text-sm text-[#868E96] py-4" data-testid="crm-pipeline-empty">Aucune opportunité pour l'instant — créez-en une depuis le bouton « Opportunité » ou depuis une fiche contact.</div>}
    </div>
  );
}
